import { apiClient } from "@/lib/api-client";
import type {
  ApplicantRegistration,
  ApplicationReceipt,
  ApplyPayload,
  Candidate,
  CandidateAuthResponse,
  CandidateErrorResponse,
  CandidateLoginPayload,
  JobApplicationSummary,
  UpdateCandidateDocumentsPayload,
  UpdateCandidateProfilePayload,
} from "@/lib/applicant";

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

function toRegistrationFormData(data: ApplicantRegistration): FormData {
  const fd = new FormData();
  fd.append("fullName", data.fullName);
  fd.append("mobile", data.mobile);
  fd.append("email", data.email);
  fd.append("dob", data.dob);
  fd.append("password", data.password);
  fd.append("address", data.address);
  fd.append("city", data.city);
  fd.append("state", data.state);
  fd.append("pin", data.pin);
  fd.append("qualification", data.qualification);
  fd.append("university", data.university);
  fd.append("passingYear", data.passingYear);
  fd.append("percentage", data.percentage);
  fd.append("candidateType", data.candidateType);
  if (data.candidateType === "experienced") {
    fd.append("employer", data.employer);
    fd.append("totalExp", data.totalExp);
    fd.append("relevantExp", data.relevantExp);
    fd.append("currentCtc", data.currentCtc);
    fd.append("expectedCtc", data.expectedCtc);
  }
  fd.append("agree", String(data.agree));
  if (data.resume) fd.append("resume", data.resume);
  if (data.photo) fd.append("photo", data.photo);
  if (data.aadhaar) fd.append("aadhaar", data.aadhaar);
  if (data.pan) fd.append("pan", data.pan);
  if (data.certificates) fd.append("certificates", data.certificates);
  return fd;
}

export async function registerCandidate(data: ApplicantRegistration): Promise<CandidateAuthResponse> {
  const res = await apiClient.post<CandidateAuthResponse | CandidateErrorResponse>(
    "/candidate/register",
    toRegistrationFormData(data),
  );
  if (!res.data.success) {
    throw new ApiError(res.data.message ?? "Something went wrong. Please try again.", res.status);
  }
  return res.data;
}

export async function loginCandidate(payload: CandidateLoginPayload): Promise<CandidateAuthResponse> {
  const res = await apiClient.post<CandidateAuthResponse | CandidateErrorResponse>("/candidate/login", payload);
  if (!res.data.success) {
    throw new ApiError(res.data.message ?? "Something went wrong. Please try again.", res.status);
  }
  return res.data;
}

export async function getCandidateProfile(token: string): Promise<Candidate> {
  const res = await apiClient.get<{ success: boolean; candidate?: Candidate; message?: string }>(
    "/candidate/profile",
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.data.success || !res.data.candidate) {
    throw new ApiError(res.data.message ?? "Unable to load your profile.", res.status);
  }
  return res.data.candidate;
}

export async function updateCandidateProfile(
  token: string,
  payload: UpdateCandidateProfilePayload,
): Promise<Candidate> {
  const res = await apiClient.put<{ success: boolean; candidate?: Candidate; message?: string }>(
    "/candidate/profile",
    payload,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.data.success || !res.data.candidate) {
    throw new ApiError(res.data.message ?? "Unable to update your profile.", res.status);
  }
  return res.data.candidate;
}

function toDocumentsFormData(payload: UpdateCandidateDocumentsPayload): FormData {
  const fd = new FormData();
  if (payload.resume) fd.append("resume", payload.resume);
  if (payload.photo) fd.append("photo", payload.photo);
  if (payload.aadhaar) fd.append("aadhaar", payload.aadhaar);
  if (payload.pan) fd.append("pan", payload.pan);
  if (payload.certificates) fd.append("certificates", payload.certificates);
  return fd;
}

export async function updateCandidateDocuments(
  token: string,
  payload: UpdateCandidateDocumentsPayload,
): Promise<Candidate> {
  const res = await apiClient.put<{ success: boolean; candidate?: Candidate; message?: string }>(
    "/candidate/profile/documents",
    toDocumentsFormData(payload),
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.data.success || !res.data.candidate) {
    throw new ApiError(res.data.message ?? "Unable to update your documents.", res.status);
  }
  return res.data.candidate;
}

function toApplyFormData(payload: ApplyPayload): FormData {
  const fd = new FormData();
  fd.append("job_id", String(payload.jobId));
  if (payload.coverLetter) fd.append("cover_letter", payload.coverLetter);
  if (payload.portfolioUrl) fd.append("portfolio_url", payload.portfolioUrl);
  if (payload.linkedinUrl) fd.append("linkedin_url", payload.linkedinUrl);
  if (payload.noticePeriod) fd.append("notice_period", payload.noticePeriod);
  if (payload.resume) fd.append("resume", payload.resume);
  return fd;
}

export async function applyToJob(
  jobOpeningCode: string,
  payload: ApplyPayload,
  token: string,
): Promise<ApplicationReceipt> {
  const res = await apiClient.post<
    { success: boolean; message?: string; application?: ApplicationReceipt } | CandidateErrorResponse
  >(`/job-postings/${encodeURIComponent(jobOpeningCode)}/apply`, toApplyFormData(payload), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.data.success || !("application" in res.data) || !res.data.application) {
    throw new ApiError(res.data.message ?? "Something went wrong. Please try again.", res.status);
  }
  return res.data.application;
}

export async function getMyApplications(token: string): Promise<JobApplicationSummary[]> {
  const res = await apiClient.get<{ success: boolean; resp?: JobApplicationSummary[]; message?: string }>(
    "/candidate/applications",
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.data.success || !res.data.resp) {
    throw new ApiError(res.data.message ?? "Unable to load your applications.", res.status);
  }
  return res.data.resp;
}
